import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { useChurchPlanner, usePlannerRoles, type PlannerAssignment } from '../../hooks/useChurchPlanner';
import { PLANNER_TEAM_DEFINITIONS, resolveTeamIdFromRole } from '../../hooks/usePlannerInsights';
import { supabase } from '../../services/supabase';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerKicker,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';

type AssignableMember = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
  roles?: string[] | null;
  default_service_plan_role_id?: string | null;
};

const SummaryCard = styled(PlannerCard)`
  display: grid;
  gap: 0.5rem;
`;

const Score = styled.span`
  font-size: 2rem;
  font-weight: 800;
  color: ${plannerColors.accent};
`;

const RoleList = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const RoleRow = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.8rem;
  background: ${plannerColors.surfaceHover};
  display: grid;
  gap: 0.55rem;
`;

const RoleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
`;

const RoleName = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  color: ${plannerColors.text};
`;

const RoleStatus = styled.span<{ $tone: 'ok' | 'warn' | 'bad' }>`
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.4)'
        : $tone === 'warn'
          ? 'rgba(255, 204, 0, 0.45)'
          : 'rgba(255, 69, 58, 0.45)'};
  color: ${({ $tone }) =>
    $tone === 'ok' ? plannerColors.success : $tone === 'warn' ? plannerColors.warning : plannerColors.danger};
  background: ${({ $tone }) =>
    $tone === 'ok'
      ? 'rgba(52, 199, 89, 0.14)'
      : $tone === 'warn'
        ? 'rgba(255, 204, 0, 0.12)'
        : 'rgba(255, 69, 58, 0.12)'};
`;

const PersonName = styled.div`
  font-size: 0.86rem;
  color: ${plannerColors.text};
  font-weight: 600;
`;

const Meta = styled.div`
  font-size: 0.74rem;
  color: ${plannerColors.muted};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const ActionButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryActionButton = styled(ActionButton)`
  border-color: rgba(141, 176, 255, 0.5);
  background: rgba(141, 176, 255, 0.18);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
`;

const BackLink = styled(Link)`
  color: ${plannerColors.accent};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  text-decoration: none;
`;

const PickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 45;
  padding: 1rem;
`;

const PickerCard = styled.div`
  width: min(560px, 100%);
  max-height: 80vh;
  background: ${plannerColors.surface};
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PickerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PickerTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${plannerColors.text};
`;

const PickerClose = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 8px;
  padding: 0.35rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
`;

const PickerSearch = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  font-size: 0.88rem;
  padding: 0.55rem 0.65rem;
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
`;

const PickerItem = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.6rem 0.7rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${plannerColors.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function localDateKey(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function overlapsServiceDate(serviceDate: string, startsAt: string, endsAt: string): boolean {
  const serviceMs = new Date(serviceDate).getTime();
  const startMs = new Date(startsAt).getTime();
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(serviceMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function pickRoleAssignment(assignments: PlannerAssignment[]): PlannerAssignment | null {
  if (!assignments.length) return null;

  const scored = assignments
    .map((assignment) => {
      const status = normalizePlannerStatus(assignment.status);
      const hasUser = Boolean(assignment.user_id);
      const isFilled = hasUser && status !== 'unavailable';
      const score = isFilled ? 3 : hasUser ? 2 : 1;
      return { assignment, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored[0]?.assignment ?? null;
}

export default function ChurchPlannerTeamHealthDetailPage() {
  const { serviceId, teamId } = useParams<{ serviceId: string; teamId: string }>();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);

  const [pickerRoleId, setPickerRoleId] = useState<string | null>(null);
  const [pickerAssignmentId, setPickerAssignmentId] = useState<string | null>(null);
  const [pickerMembers, setPickerMembers] = useState<AssignableMember[]>([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageAssignments = hasAnyPermission(roles, [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS]);
  const currentUserManagedTeamIds = useMemo(() => {
    if (!user?.id) return [] as string[];
    return Array.from(
      new Set(
        (planner.teamManagersQuery.data ?? [])
          .filter((row) => row.user_id === user.id)
          .map((row) => row.team_id)
          .filter(Boolean)
      )
    );
  }, [planner.teamManagersQuery.data, user?.id]);
  const canManageScopedAssignments = canManageAssignments || currentUserManagedTeamIds.includes(teamId || '');

  const selectedTeam = useMemo(
    () => PLANNER_TEAM_DEFINITIONS.find((team) => team.id === teamId) ?? null,
    [teamId]
  );

  const selectedPlan = useMemo(
    () => planner.plans.find((plan) => plan.id === serviceId) ?? null,
    [planner.plans, serviceId]
  );

  const roleById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; area?: string | null }>();
    (rolesQuery.data ?? []).forEach((role) => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesQuery.data]);

  const requiredRoleIds = useMemo(() => {
    if (!teamId) return [] as string[];
    const rows = (planner.teamRequiredRolesQuery.data ?? []).filter((row) => row.team_id === teamId);
    return Array.from(new Set(rows.map((row) => row.role_id).filter(Boolean)));
  }, [planner.teamRequiredRolesQuery.data, teamId]);

  const roleRows = useMemo(() => {
    if (!selectedPlan) return [] as Array<{
      roleId: string;
      roleName: string;
      assignment: PlannerAssignment | null;
      status: string;
      isFilled: boolean;
      isMissing: boolean;
      memberName: string;
      memberMeta: string;
    }>;

    return requiredRoleIds
      .map((roleId) => {
        const role = roleById.get(roleId);
        const roleName = role?.name?.trim() || 'Unknown role';
        const assignmentsForRole = (selectedPlan.service_plan_assignments ?? []).filter(
          (assignment) => assignment.role_id === roleId
        );
        const assignment = pickRoleAssignment(assignmentsForRole);
        const assignedUser = assignment ? firstRelation(assignment.assigned_user) : null;
        const normalizedStatus = normalizePlannerStatus(assignment?.status);
        const isFilled = Boolean(assignment?.user_id) && normalizedStatus !== 'unavailable';
        const isMissing = !isFilled;

        return {
          roleId,
          roleName,
          assignment,
          status: normalizedStatus,
          isFilled,
          isMissing,
          memberName: assignedUser?.full_name || assignedUser?.email || (assignment?.user_id ? 'Assigned member' : 'Unassigned'),
          memberMeta: assignedUser?.title || (assignment?.user_id ? formatPlannerStatusLabel(normalizedStatus) : 'No assignment yet'),
        };
      })
      .sort((a, b) => {
        if (a.isMissing !== b.isMissing) return a.isMissing ? -1 : 1;
        return a.roleName.localeCompare(b.roleName);
      });
  }, [requiredRoleIds, roleById, selectedPlan]);

  const availabilityByUserId = useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedPlan?.id) return map;
    (planner.availability ?? []).forEach((entry) => {
      if (entry.service_plan_id !== selectedPlan.id || !entry.user_id) return;
      map.set(entry.user_id, (entry.availability_status || '').trim().toLowerCase());
    });
    return map;
  }, [planner.availability, selectedPlan?.id]);

  const calendarUnavailableByUserId = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!selectedPlan) return map;
    (planner.memberUnavailability ?? []).forEach((entry) => {
      if (!entry.user_id) return;
      if (!overlapsServiceDate(selectedPlan.service_date, entry.starts_at, entry.ends_at)) return;
      map.set(entry.user_id, true);
    });
    return map;
  }, [planner.memberUnavailability, selectedPlan]);

  const selectedPlanAssignmentsByUser = useMemo(() => {
    const map = new Map<string, PlannerAssignment[]>();
    if (!selectedPlan) return map;
    (selectedPlan.service_plan_assignments ?? []).forEach((assignment) => {
      if (!assignment.user_id) return;
      const entries = map.get(assignment.user_id) ?? [];
      entries.push(assignment);
      map.set(assignment.user_id, entries);
    });
    return map;
  }, [selectedPlan]);

  const dayAssignedPlanIdsByUser = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!selectedPlan) return map;
    const targetDayKey = localDateKey(selectedPlan.service_date);
    planner.plans.forEach((plan) => {
      if (localDateKey(plan.service_date) !== targetDayKey) return;
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (!assignment.user_id) return;
        const planIds = map.get(assignment.user_id) ?? new Set<string>();
        planIds.add(plan.id);
        map.set(assignment.user_id, planIds);
      });
    });
    return map;
  }, [planner.plans, selectedPlan]);

  const loadPickerMembers = async (searchTerm: string) => {
    setPickerLoading(true);
    const term = searchTerm.trim();
    let query = supabase
      .from('users_directory')
      .select('id, full_name, email, title, avatar, roles, default_service_plan_role_id')
      .overlaps('roles', ['service_planner', 'service_planner_admin'])
      .order('full_name', { ascending: true })
      .limit(80);

    if (term) {
      query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
    }

    const { data, error } = await query;
    setPickerLoading(false);

    if (error) {
      window.alert(error.message || 'Unable to load planner members.');
      return;
    }

    const filteredMembers = ((data as AssignableMember[]) || []).filter((member) => {
      if (canManageAssignments) return true;
      const role = member.default_service_plan_role_id ? roleById.get(member.default_service_plan_role_id) : null;
      return resolveTeamIdFromRole(role) === teamId;
    });

    setPickerMembers(filteredMembers);
  };

  const openAssignPicker = async (roleId: string, assignmentId?: string | null) => {
    if (!canManageScopedAssignments) return;
    setPickerRoleId(roleId);
    setPickerAssignmentId(assignmentId ?? null);
    setPickerSearch('');
    await loadPickerMembers('');
  };

  const closeAssignPicker = () => {
    setPickerRoleId(null);
    setPickerAssignmentId(null);
    setPickerSearch('');
    setPickerMembers([]);
  };

  const getMemberBlockReason = (member: AssignableMember): string | null => {
    if (!pickerRoleId || !selectedPlan) return null;

    if ((member.default_service_plan_role_id || null) !== pickerRoleId) {
      return 'Default role does not match this role slot.';
    }

    const availabilityStatus = availabilityByUserId.get(member.id) || '';
    if (availabilityStatus === 'unavailable') {
      return 'Marked unavailable for this service.';
    }

    if (calendarUnavailableByUserId.get(member.id)) {
      return 'Blocked by calendar unavailability range.';
    }

    const samePlanAssignments = selectedPlanAssignmentsByUser.get(member.id) ?? [];
    const hasOtherSlotOnService = samePlanAssignments.some(
      (assignment) => assignment.id !== pickerAssignmentId
    );
    if (hasOtherSlotOnService) {
      return 'Already assigned to another slot in this service.';
    }

    const planIds = dayAssignedPlanIdsByUser.get(member.id);
    if (planIds && (planIds.size > 1 || (planIds.size === 1 && !planIds.has(selectedPlan.id)))) {
      return 'Already assigned to another service on this date.';
    }

    return null;
  };

  const handlePickMember = async (member: AssignableMember) => {
    if (!selectedPlan || !pickerRoleId) return;

    const blockReason = getMemberBlockReason(member);
    if (blockReason) {
      window.alert(blockReason);
      return;
    }

    try {
      setSavingRoleId(pickerRoleId);
      if (pickerAssignmentId) {
        await planner.updateAssignment({
          assignmentId: pickerAssignmentId,
          updates: { user_id: member.id, status: 'pending' },
        });
      } else {
        await planner.createAssignment({
          service_plan_id: selectedPlan.id,
          role_id: pickerRoleId,
          user_id: member.id,
          status: 'pending',
        });
      }
      closeAssignPicker();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to assign member.');
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleUnassign = async (assignmentId: string, roleId: string) => {
    try {
      setSavingRoleId(roleId);
      await planner.deleteAssignment(assignmentId);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to unassign member.');
    } finally {
      setSavingRoleId(null);
    }
  };

  const coverage = useMemo(() => {
    if (!roleRows.length) return 0;
    const filledCount = roleRows.filter((row) => row.isFilled).length;
    return Math.round((filledCount / roleRows.length) * 100);
  }, [roleRows]);

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading team health details...</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Unable to load team health details right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (!serviceId || !teamId || !selectedTeam) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>This team detail link is invalid.</PlannerError>
          <BackLink to="/church-planner/dashboard">Back to dashboard</BackLink>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (!selectedPlan) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Service plan could not be found.</PlannerError>
          <BackLink to="/church-planner/dashboard">Back to dashboard</BackLink>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (!canManageAssignments && !currentUserManagedTeamIds.includes(teamId)) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>You do not have access to this team.</PlannerError>
          <BackLink to="/church-planner/dashboard">Back to dashboard</BackLink>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <>
      <PlannerPage>
        <HeaderRow>
          <BackLink to="/church-planner/dashboard">Back to Dashboard</BackLink>
          {canManageScopedAssignments ? (
            <BackLink to={`/church-planner/services/${selectedPlan.id}`}>Open Full Service</BackLink>
          ) : null}
        </HeaderRow>

        <SummaryCard>
          <PlannerKicker>{(selectedPlan.title || 'Service').toUpperCase()}</PlannerKicker>
          <PlannerHeading>{selectedTeam.label}</PlannerHeading>
          <Score>{coverage}%</Score>
          <PlannerSubtle>
            {roleRows.filter((row) => row.isFilled).length} of {roleRows.length} required roles are currently filled.
          </PlannerSubtle>
        </SummaryCard>

        <PlannerCard>
          <PlannerHeading style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Required Roles</PlannerHeading>
          {!roleRows.length ? (
            <PlannerSubtle>No required roles are configured for this team yet.</PlannerSubtle>
          ) : (
            <RoleList>
              {roleRows.map((row) => {
                const statusTone: 'ok' | 'warn' | 'bad' = row.isFilled
                  ? 'ok'
                  : row.status === 'unavailable'
                    ? 'bad'
                    : 'warn';
                const statusLabel = row.isFilled
                  ? 'Assigned'
                  : row.status === 'unavailable'
                    ? 'Needs replacement'
                    : 'Missing';

                return (
                  <RoleRow key={row.roleId}>
                    <RoleHeader>
                      <RoleName>{row.roleName}</RoleName>
                      <RoleStatus $tone={statusTone}>{statusLabel}</RoleStatus>
                    </RoleHeader>
                    <PersonName>{row.memberName}</PersonName>
                    <Meta>{row.memberMeta}</Meta>
                    {canManageScopedAssignments ? (
                      <Actions>
                        <PrimaryActionButton
                          type="button"
                          onClick={() => void openAssignPicker(row.roleId, row.assignment?.user_id ? row.assignment.id : null)}
                          disabled={savingRoleId === row.roleId}
                        >
                          {row.assignment?.user_id ? 'Change' : 'Assign'}
                        </PrimaryActionButton>
                        {row.assignment?.id && row.assignment.user_id ? (
                          <ActionButton
                            type="button"
                            onClick={() => void handleUnassign(row.assignment!.id, row.roleId)}
                            disabled={savingRoleId === row.roleId}
                          >
                            Unassign
                          </ActionButton>
                        ) : null}
                      </Actions>
                    ) : null}
                  </RoleRow>
                );
              })}
            </RoleList>
          )}
        </PlannerCard>
      </PlannerPage>

      {pickerRoleId ? (
        <PickerOverlay onClick={closeAssignPicker}>
          <PickerCard onClick={(event) => event.stopPropagation()}>
            <PickerHeader>
              <PickerTitle>Assign Member</PickerTitle>
              <PickerClose type="button" onClick={closeAssignPicker}>Close</PickerClose>
            </PickerHeader>
            <PickerSearch
              value={pickerSearch}
              onChange={(event) => {
                const next = event.target.value;
                setPickerSearch(next);
                void loadPickerMembers(next);
              }}
              placeholder="Search by name or email"
            />
            <PickerList>
              {pickerLoading ? <PlannerSubtle>Loading planner members...</PlannerSubtle> : null}
              {!pickerLoading && !pickerMembers.length ? (
                <PlannerSubtle>No planner users found.</PlannerSubtle>
              ) : null}
              {pickerMembers.map((member) => {
                const blockReason = getMemberBlockReason(member);
                return (
                  <PickerItem
                    key={member.id}
                    type="button"
                    disabled={Boolean(blockReason)}
                    onClick={() => void handlePickMember(member)}
                  >
                    <strong>{member.full_name || member.email || member.id}</strong>
                    <PlannerSubtle style={{ margin: '0.2rem 0 0' }}>
                      {(member.email || 'No email')}
                      {member.title ? ` • ${member.title}` : ''}
                    </PlannerSubtle>
                    {blockReason ? (
                      <PlannerSubtle style={{ margin: '0.2rem 0 0', color: plannerColors.danger }}>
                        {blockReason}
                      </PlannerSubtle>
                    ) : null}
                  </PickerItem>
                );
              })}
            </PickerList>
          </PickerCard>
        </PickerOverlay>
      ) : null}
    </>
  );
}
